import React, {useState} from 'react'
import {Box, Button, Chip, TextField, Typography} from '@mui/material'
import type {SiteBuilderOptionsData, SiteBuilderOption} from './types'

interface SiteBuilderOptionsWidgetProps {
  data: SiteBuilderOptionsData
  onSubmit: (formattedMessage: string) => void
}

const SiteBuilderOptionsWidget: React.FC<SiteBuilderOptionsWidgetProps> = ({data, onSubmit}) => {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [freetext, setFreetext] = useState('')

  const handleOptionClick = (option: SiteBuilderOption) => {
    if (option.disabled) return

    if (data.type === 'single') {
      setSelected(new Set([option.id]))
    } else if (data.type === 'multi') {
      setSelected(prev => {
        const next = new Set(prev)
        if (next.has(option.id)) next.delete(option.id)
        else next.add(option.id)
        return next
      })
    }
  }

  const handleSubmit = () => {
    if (data.type === 'freetext') {
      if (!freetext.trim()) return
      onSubmit(freetext.trim())
      return
    }

    if (selected.size === 0) return

    const selectedOptions = data.options.filter(o => selected.has(o.id))
    const labels = selectedOptions.map(o => o.label)
    onSubmit(labels.join(', '))
  }

  const canSubmit = data.type === 'freetext'
    ? freetext.trim().length > 0
    : selected.size > 0

  return (
    <Box
      sx={{
        mx: 0,
        mb: 1,
        p: 2,
        border: 1,
        borderColor: 'primary.light',
        borderRadius: 2,
        bgcolor: 'background.paper',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}
    >
      <Typography variant="body2" fontWeight={600} sx={{mb: 1.5}}>
        {data.question}
      </Typography>

      {data.type === 'freetext' ? (
        <TextField
          fullWidth
          size="small"
          value={freetext}
          onChange={e => setFreetext(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              handleSubmit()
            }
          }}
          placeholder="Type your answer..."
          sx={{mb: 1.5}}
        />
      ) : (
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5}}>
          {data.options.map(option => (
            <Chip
              key={option.id}
              label={option.disabled ? `${option.label} (coming soon)` : option.label}
              onClick={() => handleOptionClick(option)}
              disabled={option.disabled}
              variant={selected.has(option.id) ? 'filled' : 'outlined'}
              color={selected.has(option.id) ? 'primary' : 'default'}
              sx={{
                cursor: option.disabled ? 'not-allowed' : 'pointer',
                ...(option.description ? {
                  height: 'auto',
                  '& .MuiChip-label': {
                    display: 'block',
                    whiteSpace: 'normal',
                    py: 0.75
                  }
                } : {})
              }}
            />
          ))}
        </Box>
      )}

      {/* Show descriptions for options with them */}
      {data.type !== 'freetext' && selected.size > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 1}}>
          {data.options.find(o => selected.has(o.id))?.description}
        </Typography>
      )}

      <Button
        variant="contained"
        size="small"
        onClick={handleSubmit}
        disabled={!canSubmit}
        sx={{textTransform: 'none'}}
      >
        Submit
      </Button>
    </Box>
  )
}

export default SiteBuilderOptionsWidget
